<script lang="ts" setup>
import { useSidebar } from "../composables/useSidebar";

import VIcon from "@/shared/ui/common/VIcon.vue";

interface Props {
  /** Brand logo URL */
  logo?: string
  /** Brand name */
  brandName?: string
  /** Show close button (for mobile) */
  showClose?: boolean
}

withDefaults(defineProps<Props>(), {
  logo: "",
  brandName: "App",
  showClose: false,
});

const { isCollapsed, toggleCollapse, closeMobile } = useSidebar();

const handleClose = () => {
  closeMobile();
};
</script>

<template>
  <div
    :class="isCollapsed ? 'justify-center' : 'justify-between'"
    class="sidebar-header flex items-center p-4"
  >
    <!-- Logo & Brand (visible when expanded) -->
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="!isCollapsed"
        class="flex items-center gap-3 min-w-0"
      >
        <!-- Logo -->
        <div
          v-if="logo"
          class="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden"
        >
          <img
            :alt="brandName"
            :src="logo"
            class="w-full h-full object-cover"
          >
        </div>

        <!-- Brand Name -->
        <span class="font-semibold text-lg text-neutral truncate text-gradient-animated">
          {{ brandName }}
        </span>
      </div>
    </Transition>

    <!-- Controls -->
    <div class="flex items-center gap-2 flex-shrink-0">
      <!-- Close Button (Mobile) -->
      <button
        v-if="showClose"
        aria-label="Close sidebar"
        class="p-2 rounded-lg hover:bg-base-200 text-neutral transition-colors"
        type="button"
        @click="handleClose"
      >
        <VIcon
          :size="20"
          icon="mdi:close"
        />
      </button>

      <!-- Toggle Collapse Button (Desktop) -->
      <button
        v-else
        :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        class="p-2 rounded-lg hover:bg-base-200 text-neutral transition-colors"
        type="button"
        @click="toggleCollapse"
      >
        <VIcon
          :icon="isCollapsed ? 'mdi:menu' : 'mdi:chevron-left'"
          :size="20"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
.sidebar-header {
  min-height: 65px;
}
</style>
